/* Copyright (c) 2020-present CloudIO Inc */

import {
  DataRecord,
  GenericDataRecord,
  isNumberType,
} from '@cloudio-saas/datasource-types';
import {
  BaseProps,
  Form,
  Select,
  SelectReturnValueMapping,
} from 'componentregistry';
import {
  useAttribute,
  useAttributeDirty,
  useCurrentDataStore,
  useIsEditable,
  useStoreRecords,
} from 'components/DataStoreReference/DataStoreReference';
import { HorizontalFormField } from 'components/Form/FormUtils';
import { FieldWrapper, useFieldVariant } from 'components/TextField/BaseField';
import {
  Option,
  SelectInputProps,
  VirtualizedSelectInput,
} from 'custom/SelectInput';
import { useStringExpression } from 'expression/useExpression';
import { useFocus } from 'hooks/useFocus';
import React, { memo, useEffect, useId, useMemo, useState } from 'react';
import { useChildren, useVisibleChildItems } from 'registry/Components';
import Store from 'store/Store';
import { useErrorMessage, useStoreCurrentRecordId } from 'store/useAppStore';
import useTrigger from 'triggers/useTrigger';
import WaveDots from 'utils/WaveDots';
import { useAudit } from '../../root/Audit';
import { useTranslate } from '../../utils/lang';
import { parseOptionValues } from './SelectUtils';
import { isNotEmpty } from 'utils/isEmpty';

interface SelectProps extends Required<Select.Select>, BaseProps<'Select'> {}

interface Props extends SelectProps, Form.HorizontalFormProps {}

const EMPTY_ARRAY: string[] = [];

export const getAttributeList = (
  store: Store<GenericDataRecord> | null,
  mappings: SelectReturnValueMapping.SelectReturnValueMappingItem[],
  valueAttribute?: string,
  labelAttribute?: string,
  props?: Props,
) => {
  if (!(store && valueAttribute && labelAttribute)) {
    return EMPTY_ARRAY;
  }
  const attList = [valueAttribute];
  if (labelAttribute !== valueAttribute) {
    attList.push(labelAttribute);
  }
  mappings.forEach((mapping) => {
    if (
      mapping.props.sourceAttribute &&
      mapping.props.targetAttribute &&
      !attList.includes(mapping.props.sourceAttribute)
    ) {
      attList.push(mapping.props.sourceAttribute);
    }
  });
  return attList;
};

function IOSelect(
  props: Props,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement {
  const {
    appUid,
    autoFocus,
    className,
    comboDatasourceAlias,
    doNotPrefetch,
    domProps,
    flex,
    formHorizontal,
    insertable,
    itemId,
    labelAlignment,
    labelColor,
    labelWidth,
    optionValues,
    pageId,
    plain,
    platform,
    required,
    roleUid,
    serverValidateOnChange,
    updatable,
    viewAttribute,
    bgColorExpression,
    colorExpression,
    textFontSize,
    textBold,
    labelItalic,
    textItalic,
    labelBold,
    multi,
    limitTags,
    unknownValueMessage,
    delimiter,
    selectOptionOnBlur,
    selectOptionOnTab,
    hideSelectedOptions,
    optionsWidth,
    ignoreSpecialCharacters,
    specialCharacters,
  } = props;
  let { helperText, placeholder } = props;
  const [focused, setFocused] = useState(false);
  const { t } = useTranslate();
  helperText = t(helperText);
  // label = t(label);
  let labelResult =
    useStringExpression({
      exp: props.label,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'label',
        data: {},
      },
    }) || undefined;
  labelResult = labelResult ? t(labelResult) : '';
  placeholder = t(placeholder);

  let { labelAttribute, valueAttribute } = props;
  const dirty = useAttributeDirty(viewAttribute);
  const [value, setValue] = useAttribute<string | undefined | number>(
    viewAttribute,
    '',
  );
  const [onChangeTrigger] = useTrigger(
    { appUid, roleUid, platform, pageId, itemId },
    'TriggerOnChange',
  );
  const [afterServerValidateTrigger] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerAfterServerValidate',
  );
  const store = comboDatasourceAlias
    ? platform.getStore<GenericDataRecord>(appUid, pageId, comboDatasourceAlias)
    : null;
  const isBusy = store?.isBusy();
  const labelColorString =
    useStringExpression({
      exp: labelColor,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'labelColor',
        data: {},
      },
    }) || undefined;

  const bgColorString =
    useStringExpression({
      exp: bgColorExpression,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'bgColorExpression',
        data: {},
      },
    }) || undefined;
  const textColorString =
    useStringExpression({
      exp: colorExpression,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'colorExpression',
        data: {},
      },
    }) || undefined;

  const disabled = !useIsEditable({
    appUid,
    pageId,
    itemId,
    insertable,
    updatable,
  });

  let unknowValue =
    useStringExpression({
      exp: unknownValueMessage,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'unknownValueMessage',
        data: {},
      },
    }) || undefined;
  unknowValue = unknowValue ? t(unknowValue) : '';

  const variant = useFieldVariant(appUid);
  let options: Option[] = [];
  const baseStore = useCurrentDataStore<DataRecord>();
  const recordId = useStoreCurrentRecordId(baseStore);
  const errorMessage = useErrorMessage(baseStore, viewAttribute);

  const records = useStoreRecords(store);
  if (store) {
    options = records;
  } else if (optionValues) {
    const _optionValues = platform.evalIfExpressionString(
      appUid,
      pageId,
      itemId,
      'optionValues',
      {},
      optionValues,
    );
    options = _optionValues ? parseOptionValues(_optionValues) : [];
    valueAttribute = 'value';
    labelAttribute = 'label';
  }

  const mappings = useVisibleChildItems(
    appUid,
    pageId,
    itemId,
  ) as SelectReturnValueMapping.SelectReturnValueMappingItem[];

  const attributeList = useMemo(
    () =>
      getAttributeList(store, mappings, valueAttribute, labelAttribute, props),
    [labelAttribute, mappings, store, valueAttribute, props],
  );

  useEffect(() => {
    if (
      store &&
      attributeList.length &&
      !doNotPrefetch /* && !store.hasInitialQueryFired() */
    ) {
      if (!store.ignorePKDuplicate) store.ignorePKDuplicate = true;
      store.query({
        props: {
          selectAttributeCodeList: attributeList,
          fetchDistinct: true,
        },
      });
    }
  }, [attributeList, doNotPrefetch, store]);

  if (
    store &&
    !store.hasAutoQueryFired() &&
    attributeList.length &&
    !doNotPrefetch
  ) {
    const s = store;
    platform.quietCallback(() => {
      s.setAutoQueryFired();
      if (!s.ignorePKDuplicate) s.ignorePKDuplicate = true;
      s.query({
        props: {
          selectAttributeCodeList: attributeList,
          fetchDistinct: true,
        },
      });
    });
  }

  const inputRef = useFocus(itemId);

  const handleOnFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    setFocused(true);
  };

  const handleOnBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    setFocused(false);
  };

  const startIcons = useChildren({
    appUid,
    pageId,
    itemId,
    itemType: 'Select',
  }).filter((c) => c.props.itemType === 'StartIcon');

  const endIcons = useChildren({
    appUid,
    pageId,
    itemId,
    itemType: 'Select',
  }).filter((c) => c.props.itemType === 'EndIcon');
  const id = useId();
  const selectFieldProps: SelectInputProps<Option> = {
    id,
    disabled,
    autoFocus,
    value,
    variant,
    required,
    onChange: async (
      changedVal: string,
      option: Option | null,
      _options: Option[] | null | undefined,
    ) => {
      const oldValue = value;
      let newValue: string | number = changedVal;
      const attr = baseStore?.getAttribute(viewAttribute);
      if (recordId && baseStore?.hasError(viewAttribute, recordId)) {
        baseStore?.clearError(viewAttribute, recordId);
      }
      if (attr && isNumberType(attr.attributeType) && newValue !== '') {
        newValue = Number(newValue);
        setValue(newValue);
      } else {
        setValue(newValue);
      }
      if (recordId && baseStore) {
        let optionValue;
        if (option || _options) {
          if (option) {
            optionValue = option[labelAttribute];
          } else if (_options) {
            optionValue = _options
              .filter((o) => isNotEmpty(o[labelAttribute]))
              .map((o) => o[labelAttribute])
              .join(delimiter);
          }
        } else {
          optionValue = null;
        }
        baseStore.updateRecord(recordId, {
          [`${props.viewAttribute}_label`]: optionValue,
        });
        mappings
          .filter(
            (mapping) =>
              mapping.props.sourceAttribute && mapping.props.targetAttribute,
          )
          .forEach((mapping) => {
            if (props) {
              if (
                props.platform.evalBoolean(
                  props.appUid,
                  props.pageId,
                  mapping.itemId,
                  'executeCondition',
                  { ...props },
                  mapping.props.executeCondition,
                )
              ) {
                if (option || _options) {
                  let _optionValue;
                  if (option) {
                    _optionValue = option[mapping.props.sourceAttribute];
                  } else if (_options) {
                    _optionValue = _options
                      .filter((o) =>
                        isNotEmpty(o[mapping.props.sourceAttribute]),
                      )
                      .map((v) => v[mapping.props.sourceAttribute])
                      .join(delimiter);
                  }
                  baseStore.updateRecord(recordId, {
                    [mapping.props.targetAttribute]: _optionValue,
                  });
                } else {
                  baseStore.updateRecord(recordId, {
                    [mapping.props.targetAttribute]: null,
                  });
                }
              }
            }
          });
      }
      await onChangeTrigger?.(undefined, {
        oldValue: value,
        value: newValue,
      }).then((result) => {
        if (!result) {
          setValue(oldValue);
        }
      });
      if (serverValidateOnChange && baseStore) {
        await baseStore.serverValidate({ attribute: viewAttribute, oldValue });
        await afterServerValidateTrigger?.();
      }
      if (platform.isRecording()) {
        const _selectedOptions = multi
          ? _options || []
          : option
          ? [option]
          : [];
        const labelGetter = (o: Option) => o[labelAttribute];
        _selectedOptions
          .filter((o) => isNotEmpty(labelGetter(o)))
          .forEach((o) => {
            const labelValue = multi
              ? [labelGetter(o)]
              : Array.isArray(o)
              ? o.map(labelGetter).filter(Boolean).join(delimiter)
              : labelGetter(o);
            platform.recordLine(
              itemId,
              `await ui.type('${itemId}', '${labelValue}');`,
            );
          });
      }
    },
    IOInputProps: {
      inputRef,
      style: {
        backgroundColor: bgColorString,
        color: textColorString,
        fontSize: textFontSize,
        fontWeight: textBold ? 800 : undefined,
        fontStyle: textItalic ? 'italic' : undefined,
      },
      sx: plain
        ? {
            '&.Mui-disabled': {
              paddingRight: '0px !important',
              '& > input': {
                color: 'text.primary',
                WebkitTextFillColor: textColorString,
                paddingTop: '0px !important',
              },
              '&:before': {
                border: 0,
              },
              '& > .MuiInputAdornment-positionEnd': {
                display: 'none',
              },
            },
            ...(formHorizontal && { paddingTop: '0px !important' }),
          }
        : {
            '&.Mui-disabled': {
              '& > input': {
                WebkitTextFillColor: textColorString,
              },
            },
            ...(formHorizontal && { paddingTop: '0px !important' }),
          },
    },
    valueAttribute,
    labelAttribute,
    options,
    startIcon: isBusy ? (
      <WaveDots />
    ) : startIcons.length ? (
      startIcons
    ) : undefined,
    endIcon: endIcons.length ? endIcons : undefined,
    placeholder,
    labelColor: labelColorString,
    labelFontWeight: labelBold ? 800 : undefined,
    labelFontStyle: labelItalic,
    multi,
    limitTags,
    unknownValueMessage: unknowValue,
    delimiter,
    selectOptionOnBlur,
    selectOptionOnTab,
    hideSelectedOptions,
    ignoreSpecialCharacters,
    specialCharacters,
  };
  useAudit({ appUid, pageId, active: focused, viewAttribute });

  return (
    <FieldWrapper
      className={className}
      dirty={dirty && !disabled}
      domProps={domProps}
      flex={flex}
      ref={ref}
    >
      {formHorizontal ? (
        <HorizontalFormField
          control={
            <VirtualizedSelectInput
              {...selectFieldProps}
              label={labelResult}
              formHorizontal={formHorizontal}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              optionsWidth={optionsWidth}
            />
          }
          helperText={errorMessage || helperText}
          label={labelResult}
          labelAlignment={labelAlignment}
          labelWidth={labelWidth}
          required={required}
          labelColor={labelColorString}
          fontWeight={labelBold ? 800 : undefined}
          fontStyle={labelItalic}
          id={id}
          error={!!errorMessage}
        />
      ) : (
        <VirtualizedSelectInput
          {...selectFieldProps}
          label={labelResult}
          helpText={errorMessage || helperText}
          error={!!errorMessage}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          optionsWidth={optionsWidth}
        />
      )}
    </FieldWrapper>
  );
}

export default memo(React.forwardRef(IOSelect));
